import { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useGlobalLocation } from '../../hooks/useGlobalLocation';
import { notificationDatabaseService } from '../../services/notificationDatabaseService';
import Icon from '../AppIcon';
import MashhadLogo from '../MashhadLogo';
import GlobalLocationModal from '../location/GlobalLocationModal';
import { cn } from '../../utils/cn';

const SCROLL_HIDE_THRESHOLD = 100;  // يختفي الزر عند النزول أكثر من هذا
const SCROLL_SHOW_THRESHOLD = 120;  // يظهر الزر عند الصعود عندما scrollY أقل من هذا (هستيريسيس)

const Header = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { authStatus, AUTH_STATE, userProfile } = useAuth();
  const { displayLabel, isLoading, isDenied } = useGlobalLocation();
  const [unreadCount, setUnreadCount] = useState(0);
  const notifChannelRef = useRef(null);

  const getNotificationsPath = () => {
    if (userProfile?.role === 'photographer') return '/photographer-dashboard/notifications';
    if (userProfile?.role === 'customer') return '/customer-dashboard/notifications';
    return '/notifications';
  };

  // تحميل عدد الإشعارات غير المقروءة للمستخدم المسجّل دخوله
  useEffect(() => {
    if (authStatus !== AUTH_STATE?.AUTHENTICATED || !userProfile?.id) {
      setUnreadCount(0);
      return;
    }
    const load = async () => {
      try {
        const data = await notificationDatabaseService.getMyNotifications();
        const count = (data || []).filter((n) => !n.isRead && !n.is_read).length;
        setUnreadCount(count);
      } catch {}
    };
    load();
    notifChannelRef.current = notificationDatabaseService.subscribeToNotifications(
      userProfile.id,
      (newNotif) => {
        if (!newNotif.isRead && !newNotif.is_read) {
          setUnreadCount((prev) => prev + 1);
        }
      }
    );
    return () => {
      if (notifChannelRef.current) {
        notificationDatabaseService.unsubscribeFromNotifications(notifChannelRef.current);
        notifChannelRef.current = null;
      }
    };
  }, [authStatus, userProfile?.id]);

  const [isLocationModalOpen, setIsLocationModalOpen] = useState(false);
  const [showFloatingLocationButton, setShowFloatingLocationButton] = useState(true);

  const isFloatingRoute =
    location.pathname === '/' || location.pathname === '/browse-photographers';
  const isPhotographerProfilePage =
    location.pathname === '/photographer-profile' || location.pathname.startsWith('/photographers/');
  const isBookingPage =
    location.pathname.startsWith('/booking-') ||
    location.pathname === '/new-booking' ||
    location.pathname.startsWith('/action/booking');
  const isPhotographerResultsPage = location.pathname === '/photographer-results';
  // Location button hidden from header globally — it remains on homepage floating variant only
  const showLocationButton = false;

  useEffect(() => {
    if (!isFloatingRoute) return;
    let rafId = null;
    const onScroll = () => {
      if (rafId != null) cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => {
        const y = window.scrollY;
        setShowFloatingLocationButton((prev) => {
          if (y <= SCROLL_SHOW_THRESHOLD) return true;
          if (y > SCROLL_HIDE_THRESHOLD) return false;
          return prev;
        });
        rafId = null;
      });
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      if (rafId != null) cancelAnimationFrame(rafId);
    };
  }, [isFloatingRoute]);

  const LocationButton = ({ className }) => (
    <div className="flex flex-col items-center gap-0.5">
      <button
        type="button"
        onClick={() => !isLoading && setIsLocationModalOpen(true)}
        disabled={isLoading}
        className={cn(
          'inline-flex items-center gap-1.5 sm:gap-2 rounded-md border bg-white/95',
          'focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:ring-offset-2',
          'transition-all duration-200 touch-manipulation',
          'px-3 py-1.5 sm:px-4 sm:py-2 max-w-[min(260px,calc(100vw-8rem))] sm:max-w-xs',
          isLoading
            ? 'border-slate-200 text-slate-400 cursor-wait'
            : isDenied
              ? 'border-amber-300 text-slate-600 hover:text-amber-700'
              : 'border-primary/30 text-slate-600 hover:text-primary/90',
          className
        )}
        style={{ boxShadow: '0 6px 20px rgba(0,0,0,0.15)' }}
        aria-label="تغيير الموقع"
      >
        {isLoading ? (
          <Icon name="Loader" size={16} className="text-slate-400 flex-shrink-0 animate-spin" />
        ) : isDenied ? (
          <Icon name="MapPinOff" size={16} className="text-amber-500 flex-shrink-0" />
        ) : (
          <Icon name="MapPin" size={16} className="text-primary flex-shrink-0" />
        )}
        <span className="text-xs sm:text-sm font-medium truncate min-w-0">
          {displayLabel}
        </span>
        {!isLoading && (
          <Icon name="ChevronDown" size={14} className="text-primary flex-shrink-0" />
        )}
      </button>
      {isDenied && (
        <p className="text-[10px] text-amber-600 font-medium">
          اسمح بالموقع من إعدادات المتصفح
        </p>
      )}
    </div>
  );

  // Floating variant: centered location button overlapping header/banner; on scroll it sits in header
  if (isFloatingRoute) {
    return (
      <header
        className="fixed left-0 right-0 z-50 bg-white border-b border-border shadow-sm overflow-visible"
        style={{ top: 'var(--app-download-banner-height, 0px)' }}
      >
        <div
          style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }}
          className="pb-3"
        >
          <div
            className="container mx-auto relative flex items-center justify-between px-4 gap-2 h-16"
            dir="rtl"
          >
            {/* Logo — keep alignment; order-1 in RTL = visual right */}
            <div className="order-1 flex-shrink-0 min-w-0 z-10">
              <div
                onClick={() => navigate('/')}
                className="cursor-pointer hover:opacity-80 transition-opacity"
              >
                <MashhadLogo variant="dark" size="lg" />
              </div>
            </div>

            {/* زر الموقع: يظهر في البداية، يختفي بعد التمرير قليلاً مع انتقال سلس */}
            <div
              className={cn(
                'absolute left-1/2 -translate-x-1/2 top-full translate-y-3 flex justify-center z-[60] transition-opacity duration-300',
                showFloatingLocationButton ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
              )}
            >
              <LocationButton />
            </div>

            <nav className="hidden md:flex items-center gap-2 order-3 flex-shrink-0">
              {authStatus === AUTH_STATE?.AUTHENTICATED ? (
                <button
                  onClick={() => navigate(getNotificationsPath())}
                  className="text-foreground transition-colors relative p-2 rounded-lg hover:bg-slate-100"
                  onMouseEnter={(e) => (e.target.style.color = 'var(--color-link)')}
                  onMouseLeave={(e) => (e.target.style.color = '')}
                >
                  <Icon name="Bell" size={20} />
                </button>
              ) : null}
            </nav>
            {/* Spacer on mobile so logo doesn’t collide with center button */}
            <div className="order-2 flex-1 md:hidden min-w-[2.5rem]" aria-hidden />
          </div>
        </div>

        {isLocationModalOpen && (
          <GlobalLocationModal
            isOpen={isLocationModalOpen}
            onClose={() => setIsLocationModalOpen(false)}
          />
        )}
      </header>
    );
  }

  // Default layout: logo + location under logo (other routes); لا زر موقع في صفحة ملف المصور
  return (
    <header
      className="fixed left-0 right-0 z-50 bg-white border-b border-border shadow-sm"
      style={{ top: 'var(--app-download-banner-height, 0px)' }}
    >
      <div style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }}>
        <div className={cn('container mx-auto flex items-center justify-between px-4 gap-2', showLocationButton ? 'h-24' : 'h-14')} dir="rtl">
          <div className="order-1 flex flex-col items-end gap-1 min-w-0">
            <div
              onClick={() => navigate('/')}
              className="cursor-pointer hover:opacity-80 transition-opacity"
            >
              <MashhadLogo variant="dark" size="lg" />
            </div>
            {showLocationButton && (
              <button
                type="button"
                onClick={() => setIsLocationModalOpen(true)}
                className="mt-0.5 focus:outline-none"
                aria-label="تغيير الموقع"
              >
                <div className="inline-flex items-center gap-1.5 md:gap-2 px-1 py-0.5 max-w-full">
                  <Icon name="MapPin" size={16} className="text-primary flex-shrink-0" />
                  <span className="text-[11px] md:text-xs lg:text-sm font-medium text-primary truncate max-w-[160px] md:max-w-xs lg:max-w-sm">
                    {displayLabel}
                  </span>
                  <Icon name="ChevronDown" size={14} className="text-primary flex-shrink-0" />
                </div>
              </button>
            )}
          </div>

          <nav className="flex items-center gap-2 order-3">
            {authStatus === AUTH_STATE?.AUTHENTICATED ? (
              <button
                onClick={() => navigate(getNotificationsPath())}
                className="relative p-2 rounded-lg hover:bg-slate-100 text-foreground transition-colors"
                aria-label="الإشعارات"
              >
                <Icon name="Bell" size={20} />
                {unreadCount > 0 && (
                  <span className="absolute top-1 right-1 min-w-[16px] h-4 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center px-0.5 leading-none">
                    {unreadCount > 9 ? '9+' : unreadCount}
                  </span>
                )}
              </button>
            ) : null}
          </nav>
        </div>
      </div>

      {isLocationModalOpen && (
        <GlobalLocationModal
          isOpen={isLocationModalOpen}
          onClose={() => setIsLocationModalOpen(false)}
        />
      )}
    </header>
  );
};

export default Header;
